import React from "react";

const CodeOfEthics = () => {
  const enforcementProcess = [
    {
      step: "1",
      title: "Violation Reporting",
      description:
        "When a Citizen Reporter or Professional Journalist member violates the code of ethics",
      details: [
        "Formal complaint submission",
        "Initial review by ethics committee",
        "Documentation of alleged violation",
        "Notification to accused member",
      ],
      timeline: "Within 7 days of complaint",
    },
    {
      step: "2",
      title: "Executive Board Hearing",
      description: "The case is presented to the executive board for a hearing",
      details: [
        "Presentation of evidence and findings",
        "Member's right to speak in defense",
        "Witness testimony if applicable",
        "Board deliberation process",
      ],
      timeline: "Scheduled within 30 days",
    },
    {
      step: "3",
      title: "Decision & Actions",
      description:
        "Board determines appropriate action based on violation severity",
      details: [
        "Warning for serious violations",
        "Expulsion for repeated violations",
        "Appeal process initiation",
        "Implementation of corrective measures",
      ],
      timeline: "Immediate after hearing",
    },
  ];

  const violationCategories = [
    {
      level: "Level 1",
      severity: "Minor Violations",
      examples: [
        "Unintentional factual errors",
        "Minor attribution oversights",
        "Procedural non-compliance",
        "Technical guideline breaches",
      ],
      actions: [
        "Written notice and correction",
        "Mandatory ethics training",
        "Supervised probation period",
        "Mentorship assignment",
      ],
    },
    {
      level: "Level 2",
      severity: "Serious Violations",
      examples: [
        "Intentional misinformation",
        "Plagiarism or content theft",
        "Undisclosed conflicts of interest",
        "Breach of source confidentiality",
      ],
      actions: [
        "Formal warning issued",
        "Temporary suspension",
        "Public correction mandate",
        "Review by executive board",
      ],
    },
    {
      level: "Level 3",
      severity: "Gross Violations",
      examples: [
        "Repeated serious violations",
        "Criminal activity involvement",
        "Severe ethical misconduct",
        "Threat to public safety",
      ],
      actions: [
        "Expulsion from NAI",
        "Public notification",
        "Industry-wide alert",
        "Legal referral if needed",
      ],
    },
  ];

  const memberRights = [
    {
      right: "Right to Defense",
      description:
        "The member has the right to speak in his defense during the hearing process",
      details: [
        "Personal representation at hearing",
        "Presentation of counter-evidence",
        "Cross-examination of witnesses",
        "Legal counsel assistance",
      ],
    },
    {
      right: "Appeal Process",
      description: "Members can appeal decisions through established channels",
      details: [
        "Written appeal submission",
        "Independent review committee",
        "Re-hearing possibility",
        "Final board decision",
      ],
    },
    {
      right: "Due Process",
      description:
        "Fair and transparent procedures throughout disciplinary process",
      details: [
        "Timely notification of charges",
        "Access to all evidence",
        "Impartial board members",
        "Written decision with reasoning",
      ],
    },
  ];

  const boardComposition = [
    {
      role: "Chairperson",
      responsibility: "Oversee hearing process and ensure procedural fairness",
      qualifications: "Senior journalist with 10+ years experience",
    },
    {
      role: "Legal Advisor",
      responsibility:
        "Provide legal guidance on ethical standards and procedures",
      qualifications: "Legal professional with media law expertise",
    },
    {
      role: "Industry Experts",
      responsibility:
        "Bring professional journalism perspective to deliberations",
      qualifications: "Respected media professionals from various sectors",
    },
    {
      role: "Public Representative",
      responsibility: "Ensure public interest consideration in decisions",
      qualifications: "Civil society representative with media understanding",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-900 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Code of Ethics Enforcement
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 max-w-4xl mx-auto">
              Disciplinary Procedures and Member Rights in Ethical Violation
              Cases
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ethical Standards Enforcement
            </h2>
            <div className="w-24 h-1 bg-purple-600 mx-auto rounded-full"></div>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 text-center">
            <p className="text-lg leading-relaxed mb-6">
              The NAI maintains rigorous enforcement of its Code of Ethics to
              preserve journalistic integrity and public trust. Our disciplinary
              process ensures fairness while upholding the highest professional
              standards.
            </p>
          </div>
        </div>
      </section>

      {/* Enforcement Process */}
      <section className="py-20 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Enforcement Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Structured procedure for addressing Code of Ethics violations
            </p>
            <div className="w-24 h-1 bg-purple-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {enforcementProcess.map((step) => (
              <div
                key={step.step}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 group"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-3xl font-bold text-white">
                      Step {step.step}
                    </div>
                    <div className="text-white text-sm bg-purple-800 px-3 py-1 rounded-full">
                      {step.timeline}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white">{step.title}</h3>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    {step.description}
                  </p>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
                      Process Details:
                    </h4>
                    <ul className="space-y-2">
                      {step.details.map((detail, index) => (
                        <li key={index} className="flex items-start">
                          <svg
                            className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-gray-600 text-sm">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Violation Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Violation Categories & Actions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Classification of ethical violations and corresponding
              disciplinary measures
            </p>
            <div className="w-24 h-1 bg-purple-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {violationCategories.map((category, index) => (
              <div
                key={index}
                className={`rounded-2xl p-6 border-2 ${
                  category.level === "Level 1"
                    ? "bg-green-50 border-green-200"
                    : category.level === "Level 2"
                    ? "bg-yellow-50 border-yellow-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div className="text-center mb-6">
                  <span
                    className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                      category.level === "Level 1"
                        ? "bg-green-100 text-green-800"
                        : category.level === "Level 2"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {category.level}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mt-4">
                    {category.severity}
                  </h3>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      Examples:
                    </h4>
                    <ul className="space-y-2">
                      {category.examples.map((example, exIndex) => (
                        <li
                          key={exIndex}
                          className="flex items-start text-sm text-gray-600"
                        >
                          <span className="text-gray-400 mr-2 mt-1">•</span>
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      Disciplinary Actions:
                    </h4>
                    <ul className="space-y-2">
                      {category.actions.map((action, acIndex) => (
                        <li
                          key={acIndex}
                          className="flex items-start text-sm text-gray-600"
                        >
                          <span
                            className={`mr-2 mt-1 ${
                              category.level === "Level 1"
                                ? "text-green-500"
                                : category.level === "Level 2"
                                ? "text-yellow-500"
                                : "text-red-500"
                            }`}
                          >
                            ▸
                          </span>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Member Rights */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Member Rights & Protections
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ensuring fair treatment and due process for all members in
              disciplinary proceedings
            </p>
            <div className="w-24 h-1 bg-purple-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {memberRights.map((right, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl text-purple-600 mr-3">🛡️</span>
                  {right.right}
                </h3>

                <p className="text-gray-700 leading-relaxed mb-4">
                  {right.description}
                </p>

                <ul className="space-y-2">
                  {right.details.map((detail, detailIndex) => (
                    <li
                      key={detailIndex}
                      className="flex items-start text-sm text-gray-600"
                    >
                      <svg
                        className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Board Composition */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-10 rounded-2xl p-8 backdrop-blur-sm">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">
                Executive Board Composition
              </h2>
              <p className="text-purple-100 text-lg">
                Qualified professionals ensuring fair and knowledgeable
                oversight of ethical standards
              </p>
              <div className="w-24 h-1 bg-white mx-auto mt-4 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {boardComposition.map((member, index) => (
                <div
                  key={index}
                  className="bg-white bg-opacity-20 rounded-xl p-6"
                >
                  <h3 className="font-bold text-lg mb-2">{member.role}</h3>
                  <p className="text-purple-100 text-sm mb-3">
                    {member.responsibility}
                  </p>
                  <div className="text-xs text-purple-200 bg-white bg-opacity-10 px-3 py-1 rounded-full inline-block">
                    {member.qualifications}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expulsion Criteria */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Expulsion Criteria
            </h2>
            <p className="text-xl text-gray-600">
              Conditions leading to removal from the Newsmedia Association of
              India
            </p>
            <div className="w-24 h-1 bg-purple-600 mx-auto mt-4 rounded-full"></div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl text-red-600 mr-3">⚠️</span>
                  Grounds for Expulsion
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Repeated violations after formal warning
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Gross misconduct affecting NAI reputation
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Criminal conviction related to journalism
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Simple majority vote by executive board
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl text-red-600 mr-3">📋</span>
                  Expulsion Process
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">1.</span>
                    Formal hearing with evidence presentation
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">2.</span>
                    Member defense opportunity
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">3.</span>
                    Board deliberation and voting
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">4.</span>
                    Written notification of decision
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Uphold Ethical Journalism Standards
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-300">
            Maintain the highest ethical standards and understand the
            enforcement process that protects journalistic integrity
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
              Download Full Code of Ethics
            </button>
            <button className="border-2 border-white hover:bg-white hover:text-gray-900 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
              Contact Ethics Committee
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CodeOfEthics;
